import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { NotificationMessage } from "../shared/models/models";
import { Login } from "./models/AccountModels";
import { SerializedError } from "../shared/models/errorHandlingModels";
import { login } from "./api/accountApi";
import accountContext from "./accountContext";
import { serializeError, createErrorNotification } from "../shared/utils/errorUtils";
import {
  HostGateErrorCode,
  PilotHostGateState,
  consumePilotHostGate,
  isHostGateErrorCode,
  sanitiseAllowedHost,
} from "./pilotHostGate";

interface LoginState {
  isLoading: boolean;
  formNotificationMessage?: NotificationMessage;
  showPassword: boolean;
  /**
   * Set when the server refused this sign-in because the request arrived on the wrong
   * hostname — EITHER gate: `PILOT_HOST_NOT_ALLOWED` (the user holds the MidasPilot role and
   * is off the pilot surface) or `HOST_NOT_ALLOWED` (the user is off their tenant's home
   * surface). Mutually exclusive with `formNotificationMessage` — neither is a credential
   * error and neither may render as the red "invalid credentials" alert.
   *
   * Field name kept from the pilot ticket on purpose: renaming it would churn the notice
   * component, the interceptor and their tests for zero behaviour change.
   */
  pilotHostGate?: PilotHostGateState;
}

const initialState: LoginState = {
  isLoading: false,
  formNotificationMessage: undefined,
  showPassword: false,
  // Picks up a notice stashed by the axios interceptor before it logged the user out and
  // redirected here (a 403 on an already-held session / token refresh). Consume-once.
  pilotHostGate: consumePilotHostGate(),
};

export const handleLogin = createAsyncThunk(
  "login/handleLogin",
  async (model: Login, { rejectWithValue }) => {
    try {
      const loginResult = await login(model);
      if (!loginResult) {
        return rejectWithValue(serializeError({ message: "Login failed" }));
      }

      accountContext.finishLogin(loginResult);
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    togglePasswordVisibility: (state) => {
      state.showPassword = !state.showPassword;
    },
    resetLoginState: (state) => {
      state.isLoading = false;
      state.formNotificationMessage = undefined;
      state.showPassword = false;
      state.pilotHostGate = undefined;
    },
    setFormNotificationMessage: (state, action: PayloadAction<NotificationMessage | undefined>) => {
      state.formNotificationMessage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(handleLogin.pending, (state) => {
        state.isLoading = true;
        state.formNotificationMessage = undefined;
        state.pilotHostGate = undefined;
      })
      .addCase(handleLogin.fulfilled, (state) => {
        state.isLoading = false;
        state.formNotificationMessage = undefined;
        state.pilotHostGate = undefined;
      })
      .addCase(handleLogin.rejected, (state, action) => {
        state.isLoading = false;

        const error = action.payload as SerializedError;

        // Either host gate: the refusal is not a credential failure. Show the calm
        // "sign in at <address>" notice INSTEAD of the error alert, never both. The copy is the
        // same for both codes, so the code is recorded only for diagnosis.
        if (isHostGateErrorCode(error?.code)) {
          state.pilotHostGate = {
            allowedHost: sanitiseAllowedHost(error?.details?.allowedHost),
            code: error.code as HostGateErrorCode,
          };
          state.formNotificationMessage = undefined;
          return;
        }

        state.pilotHostGate = undefined;
        state.formNotificationMessage = createErrorNotification(error);
      });
  },
});

export const {
  togglePasswordVisibility,
  resetLoginState,
  setFormNotificationMessage,
} = loginSlice.actions;

export default loginSlice.reducer;
